/**
 * Index route — single decision tree for post-authentication routing.
 *
 * Resolves the user's state via getSession + listPasskeys and routes to
 * exactly one destination:
 *   - !user                              → /login
 *   - !hasFactor                         → /enroll-mfa (AUTH-80 / AUTH-133)
 *   - hasFactor && !mfaSatisfied         → /step-up (AUTH-132 / AUTH-134)
 *   - hasFactor && mfaSatisfied + invite → explicit invitation confirmation
 *   - hasFactor && mfaSatisfied          → duplicate warning or redirect_uri
 *
 * Both password sign-in and OAuth callback land here so the gating happens
 * in one place. Avoids duplicating the decision tree in every entry point.
 */
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { orpcClient } from "@/lib/auth-client";
import {
  getGatewayInternalRedirect,
  getInvitationInboxRedirectUrl,
  safePostAuthRedirect,
} from "@/lib/redirect-utils";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { useDevOverride } from "@/lib/dev/dev-overrides";
import { AuthLoading } from "@/components/auth-loading";
import { Alert, AlertDescription, AlertTitle } from "@better-bookkeeping/ui/alert";
import { AlertCircle } from "lucide-react";
import {
  confirmPendingInvitation,
  clearPendingInviteToken,
  getPendingInvitation,
  isUnavailableInvitationError,
  withInviteTokenSearch,
} from "@/lib/pending-pricing-invite";
import { z } from "zod";
import { SignedInPanel } from "@/components/signed-in-panel";
import { Button } from "@better-bookkeeping/ui/button";

export const Route = createFileRoute("/")({
  validateSearch: withInviteTokenSearch({
    redirect_uri: z.string().optional(),
  }).parse,
  component: IndexComponent,
});

export function IndexComponent() {
  const { redirect_uri } = Route.useSearch();
  const runtimeConfig = useRuntimeConfig();
  const pendingInvitation = getPendingInvitation();

  // AUTH-122 (DEV-only): bypass renders the "you're signed in" panel so the
  // dev toolbar's screen selector lands here without bouncing to /login.
  const bypassAuthGate = useDevOverride((s) => s.bypassAuthGate);
  const devBypassActive = import.meta.env.DEV && bypassAuthGate;

  const sessionQuery = useQuery({
    queryKey: ["auth", "gateway-session"],
    queryFn: () => orpcClient.auth.getSession(),
    retry: false,
    enabled: !devBypassActive,
  });

  const passkeysQuery = useQuery({
    queryKey: ["auth", "passkeys"],
    queryFn: () => orpcClient.auth.listPasskeys(),
    retry: false,
    enabled: !devBypassActive && !!sessionQuery.data,
  });

  // AUTH-154: is the signed-in account flagged as a duplicate of another?
  const duplicateQuery = useQuery({
    queryKey: ["auth", "duplicate-check"],
    queryFn: () => orpcClient.auth.checkDuplicate(),
    retry: false,
    enabled: !devBypassActive && !!sessionQuery.data,
  });

  if (devBypassActive) {
    return <SignedInPanel data-testid="signed-in-panel" />;
  }

  if (
    sessionQuery.isLoading ||
    (sessionQuery.data && (passkeysQuery.isLoading || duplicateQuery.isLoading))
  ) {
    return <AuthLoading data-testid="gateway-loading" />;
  }

  const session = sessionQuery.data;
  if (!session) {
    return <Navigate to={pendingInvitation ? "/signup" : "/login"} search={{ redirect_uri }} />;
  }

  const passkeys = passkeysQuery.data ?? [];
  const hasFactor = session.user.twoFactorEnabled || passkeys.length > 0;
  const mfaSatisfied = session.session.mfaSatisfied;
  const destination = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? undefined;

  // AUTH-80 / AUTH-133: no factor enrolled → forced enrollment.
  if (!hasFactor) {
    return <Navigate to="/enroll-mfa" search={{ redirect_uri: destination }} />;
  }

  // AUTH-132: has factor but session hasn't satisfied MFA → step-up.
  if (!mfaSatisfied) {
    return <Navigate to="/step-up" search={{ redirect_uri: destination }} />;
  }

  // Explicit invitation confirmation must happen before any route that can
  // leave the gateway. Binding identifies the actual account but grants no access.
  if (pendingInvitation) {
    return (
      <InvitationConfirmation
        authUserId={session.user.id}
        accountEmail={session.user.email}
        destination={getInvitationInboxRedirectUrl(runtimeConfig)}
        duplicateOfEmail={
          duplicateQuery.data?.isDuplicate
            ? (duplicateQuery.data.duplicateOfEmail ?? undefined)
            : undefined
        }
      />
    );
  }

  // AUTH-154: once any invitation has been explicitly confirmed, warn a fully
  // authenticated duplicate before entering the app.
  if (duplicateQuery.data?.isDuplicate) {
    return (
      <Navigate
        to="/duplicate-account"
        search={{
          redirect_uri: destination,
          email: session.user.email,
          duplicateOfEmail: duplicateQuery.data.duplicateOfEmail ?? undefined,
        }}
      />
    );
  }

  if (destination) return <PostAuthRedirect destination={destination} />;
  return <SignedInPanel data-testid="signed-in-panel" />;
}

function InvitationConfirmation({
  authUserId,
  accountEmail,
  destination,
  duplicateOfEmail,
}: {
  authUserId: string;
  accountEmail: string;
  destination?: string;
  duplicateOfEmail?: string;
}) {
  const navigate = useNavigate();
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationUnavailable, setInvitationUnavailable] = useState(false);

  async function confirm() {
    setIsConfirming(true);
    setError(null);
    try {
      await confirmPendingInvitation(authUserId);
      if (duplicateOfEmail) {
        await navigate({
          to: "/duplicate-account",
          search: {
            redirect_uri: destination,
            email: accountEmail,
            duplicateOfEmail,
          },
        });
        return;
      }
      window.location.href = destination ?? "/";
    } catch (cause) {
      if (isUnavailableInvitationError(cause)) {
        clearPendingInviteToken();
        setInvitationUnavailable(true);
        const reason =
          cause instanceof Error ? cause.message : "This invitation is no longer active.";
        setError(
          `${reason} Ask your Visor contact for a new invitation, or continue to your account.`,
        );
      } else {
        setError(
          "This invitation could not be confirmed for the signed-in account. Try again or use another account.",
        );
      }
      setIsConfirming(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="gateway-invitation-confirmation">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Confirm invitation account</h2>
        <p className="text-sm text-muted-foreground">
          Continue with {accountEmail}. Confirming identifies your account but does not accept any
          access.
        </p>
      </div>
      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Confirmation failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {invitationUnavailable ? (
        <Button asChild className="w-full">
          <a href="/">Continue to account</a>
        </Button>
      ) : (
        <Button className="w-full" disabled={isConfirming} onClick={confirm}>
          {isConfirming ? "Confirming…" : "Confirm this account"}
        </Button>
      )}
      <Button asChild variant="outline" className="w-full">
        <a href="/login">Use another account</a>
      </Button>
    </div>
  );
}

function PostAuthRedirect({ destination }: { destination: string }) {
  const runtimeConfig = useRuntimeConfig();
  useEffect(() => {
    const internalDestination = getGatewayInternalRedirect(destination, runtimeConfig);
    window.location.href = internalDestination ?? destination;
  }, [destination, runtimeConfig]);
  return <AuthLoading data-testid="gateway-redirect-loading" />;
}
